import { useState, useEffect, useCallback } from "react";
import { useStudioErstellen } from "../../store/studioErstellenStore";
import { useStudioEditor } from "../../store/studioEditorStore";

import ErrorMessage from "./ErrorMessage";
import classes from "./Stornierungsbedingungen.module.css";

// Map enum values from backend to component values
const enumToComponentMap = {
    'FLEXIBLE': 'kulant',
    'STANDARD': 'standard',
    'STRICT': 'strikt'
};

// Map component values to enum values for backend
const componentToEnumMap = {
    'kulant': 'FLEXIBLE',
    'standard': 'STANDARD',
    'strikt': 'STRICT'
};

export const Stornierungsbedingungen = ({ validate, fromEditor, studio }) => {
    const { studioData, updateStudioData } = useStudioErstellen();
    const { updateField, updatedFields } = useStudioEditor();
    const hasUpdatedCancellationPolicy = Object.prototype.hasOwnProperty.call(updatedFields, 'cancellationPolicy');
    
    // Convert enum value from studio to component value
    const getComponentValue = useCallback((enumValue) => {
        if (!enumValue) return "";
        return enumToComponentMap[enumValue] || enumValue.toLowerCase();
    }, []);
    
    const [selectedOption, setSelectedOption] = useState(
        fromEditor 
            ? getComponentValue(hasUpdatedCancellationPolicy ? updatedFields.cancellationPolicy : studio?.cancellationPolicy)
            : (studioData.cancellationPolicy || "")
    );

    const [errors, setErrors] = useState({
        studioType: ""
    });

    const options = [
        { value: "kulant", title: "Kulant", text: "Bis 48 Stunden vor Beginn: ", text2: "Volle Rückerstattung", text3: "Bis 24 Stunden vor Beginn: ", text4: "50% Rückerstattung", text5: "Unter 24 Stunden vor Beginn: ", text6: "Keine Rückerstattung" },
        { value: "standard", title: "Standard", text: "Bis 5 Tage vor Beginn: ", text2: "Volle Rückerstattung", text3: "Bis 48 Stunden vor Beginn: ", text4: "50% Rückerstattung", text5: "Unter 48 Stunden vor Beginn: ", text6: "Keine Rückerstattung" },
        { value: "strikt", title: "Strikt", text: "Bis 14 Tage vor Beginn: ", text2: "Volle Rückerstattung", text3: "Bis 7 Tage vor Beginn: ", text4: "50% Rückerstattung", text5: "Unter 7 Tage vor Beginn: ", text6: "Keine Rückerstattung" },
    ];

    const handleValidation = () => {
        let isValid = true;
        const newErrors = {
            studioType: ""
        };

        if (!selectedOption) {
            newErrors.studioType = "Du musst eine Stornierungsbedingung auswählen.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Initialize from studio when in editor mode
    useEffect(() => {
        if (fromEditor) {
            const sourceValue = hasUpdatedCancellationPolicy
                ? updatedFields.cancellationPolicy
                : studio?.cancellationPolicy;
            setSelectedOption(getComponentValue(sourceValue));
        }
        // Rehydrate from updatedFields on remount; user clicks update local state directly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromEditor, studio, getComponentValue]);

    const handleCheckboxChange = (value) => {
        setSelectedOption(value);
        if (fromEditor) {
            // Map component value to enum value for backend
            const enumValue = componentToEnumMap[value] || value;
            updateField('cancellationPolicy', enumValue);
        } else {
            updateStudioData('cancellationPolicy', value);
        }
    };

    useEffect(() => {
        validate.current = handleValidation;
    }, [validate, selectedOption]);

  return (
    <div className={classes.mainContainer}>
        <div className={classes.titleContainer}>
            <h2 className={`${classes.title} titleSemibold2`}>Stornierungsbedingungen</h2>
            <span style={{display: fromEditor ? 'none' : 'block', color: "var(--dark-grey-4)"}} className={`${classes.text} regularSmall`}>Stornierung innerhalb von 24 Stunden nach der Buchung: Volle Rückerstattung, unabhängig vom Buchungsdatum.</span>
        </div>
        <div className={classes.bottomContainer} style={{maxWidth: fromEditor && window.innerWidth > 742 ? '340px' : '100%', width: fromEditor && window.innerWidth > 385 ? '' : '100%'}}>
            {options.map((option) => (
                <div 
                    key={option.value} 
                    className={classes.option}
                    onClick={() => handleCheckboxChange(option.value)}
                >
                    <div className={classes.inputContainer}>
                        <div className={classes.inputInsideContainer} style={{backgroundColor: selectedOption === option.value ? "#0B0418" : "transparent"}}>
                            <input
                            className={classes.inputs}
                            type="radio"
                            id={option.value}
                            name="cancellationPolicy"
                            value={option.value}
                            checked={selectedOption === option.value}
                            onChange={() => {}}    
                            />
                            {selectedOption === option.value && (
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <rect width="24" height="24" rx="12" fill="#0B0418"/>
                                <path d="M17.0099 9.21297L17.0823 9.14405L17.0134 9.07159L16.3745 8.39983L16.3055 8.32737L16.2331 8.39629L10.0275 14.2988L7.79255 12.0158L7.7226 11.9443L7.65114 12.0143L6.98864 12.6628L6.91718 12.7328L6.98713 12.8043L9.93017 15.8106L9.96722 15.8484L9.99868 15.8817L9.99925 15.8812L10.0001 15.8821L10.0621 15.8213L10.0713 15.8126L17.0099 9.21297Z" fill="white" stroke="white" strokeWidth="0.2"/>
                            </svg>
                            )}
                        </div>
                        <label style={{marginBottom: "6px"}} className="titleSemibold3">{option.title}</label>
                    </div>

                    <div className={classes.labelContainer}>
                        <div className={classes.textContainer}>
                            <label className={`${classes.label} slimRegularSmallText`}>{option.text}</label>
                            <label className={`${classes.label} slimRegularSmallText`}>{option.text2}</label>
                        </div>
                        <div className={classes.textContainer}>
                            <label className={`${classes.label} slimRegularSmallText`}>{option.text3}</label>
                            <label className={`${classes.label} slimRegularSmallText`}>{option.text4}</label>
                        </div>
                        <div className={classes.textContainer}>
                            <label className={`${classes.label} slimRegularSmallText`}>{option.text5}</label>
                            <label className={`${classes.label} slimRegularSmallText`}>{option.text6}</label>
                        </div>
                    </div>
                </div>
            ))}
            {errors.studioType && <ErrorMessage>{errors.studioType}</ErrorMessage>}
        </div>
    </div>
  );
};

export default Stornierungsbedingungen;
